/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable from "@insite/mobius/Clickable";
import Icon, { IconWrapper } from "@insite/mobius/Icon";
import { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import IconArrowRightCopper from "@stoneage/XDL/Icons/IconArrowRightCopper";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

type Props = WidgetProps & HasProduct & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: CustomAppState, ownProps: HasProduct) => ({});

export interface JettingAppLinkStyles {
    whiteContainer?: InjectableCss;
    message?: TypographyProps;
    viewLink?: LinkPresentationProps;
}

export const jettingAppLinkStyles: JettingAppLinkStyles = {
    whiteContainer: {
        css: css`
            margin-top: 12px;
            background: white;
            padding: 8px;
            &&:nth-child(2n) {
                margin-top: 20px;
            }
            @media print {
                display: none;
            }
        `,
    },
    message: {
        size: "12px",
        lineHeight: "18px",
        css: css`
            margin-bottom: 8px;
            display: block;
        `,
    },
    viewLink: {
        css: css`
            font-size: 12px;
            line-height: 18px;
            font-weight: bold;
            text-transform: capitalize;
            color: ${getColor("common.black")};

            &:hover,
            &:focus {
                color: ${getColor("primary")};
            }

            ${IconWrapper} {
                float: right;
                margin-left: 8px;
                position: relative;
            }
        `,
    },
};

const styles = jettingAppLinkStyles;

const JettingAppLink = ({ product, fields: { jettingAppLink, linkText } }: Props) => {
    if (product.properties["isJettingAppRequired"]?.toLowerCase() !== "true") {
        return null;
    }
    let link = jettingAppLink;
    if (product.properties["JettingAppId"]) {
        link = `${link}?calculatorId=${product.properties["JettingAppId"]}`;
    }

    return (
        <StyledWrapper {...styles.whiteContainer}>
            <Typography {...styles.message}>{siteMessage("Custom_PDP_JettingRequired")}</Typography>
            <Clickable href={link} target="_blank" {...styles.viewLink}>
                {linkText}
                <Icon src={IconArrowRightCopper} size={9} color="common.black" />
            </Clickable>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(connect(mapStateToProps)(JettingAppLink)),
    definition: {
        displayName: "Jetting App Link",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: "jettingAppLink",
                displayName: "Jetting App Link",
                editorTemplate: "TextField",
                defaultValue: "View Jetting App",
                fieldType: "General",
            },
            {
                name: "linkText",
                displayName: "Button Text",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
